import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Share,
  FlatList,
  Switch
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { colors, typography, spacing, borderRadius } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useTheme } from '../../context/ThemeContext';
import { createRoutineSharedNotification } from '../../services/notificationService';

type Props = NativeStackScreenProps<MainStackParamList, 'RoutineSharing'>;

interface FriendData {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  profilePhoto?: string;
  hasAccess: boolean;
}

const RoutineSharingScreen = ({ route, navigation }: Props) => {
  const { routineId } = route.params;
  const [routine, setRoutine] = useState<any>(null);
  const [friends, setFriends] = useState<FriendData[]>([]);
  const [loading, setLoading] = useState(true);
  const [friendsLoading, setFriendsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [publicSharing, setPublicSharing] = useState(false);
  const [routineCode, setRoutineCode] = useState('');
  const { theme } = useTheme();

  useEffect(() => {
    fetchRoutineDetails();
    fetchFriends();
  }, []);

  const fetchRoutineDetails = async () => {
    try {
      const routineRef = doc(firestore, 'routines', routineId);
      const routineDoc = await getDoc(routineRef);
      
      if (!routineDoc.exists()) {
        Alert.alert('Error', 'No se encontró la rutina');
        navigation.goBack();
        return;
      }
      
      const routineData = routineDoc.data();
      setRoutine(routineData);
      setPublicSharing(routineData.shared || false);
      setRoutineCode(routineData.routineCode || '');
    } catch (error) {
      console.error('Error al obtener detalles de la rutina:', error);
      Alert.alert('Error', 'No se pudieron cargar los detalles de la rutina');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchFriends = async () => {
    try {
      setFriendsLoading(true);
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Get user's friends list
      const userRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }
      
      const userData = userDoc.data();
      const friendIds = userData.friends || [];
      
      if (friendIds.length === 0) {
        setFriends([]);
        setFriendsLoading(false);
        return;
      }
      
      // Get routine authorized users
      const routineRef = doc(firestore, 'routines', routineId);
      const routineDoc = await getDoc(routineRef);
      
      if (!routineDoc.exists()) {
        throw new Error('No se encontró la rutina');
      }
      
      const routineData = routineDoc.data();
      const authorizedUsers = routineData.authorizedUsers || [];
      
      // Get friend data and check if they have access
      const friendsData: FriendData[] = [];
      
      await Promise.all(
        friendIds.map(async (friendId: string) => {
          const friendRef = doc(firestore, 'users', friendId);
          const friendDoc = await getDoc(friendRef);
          
          if (friendDoc.exists()) {
            const friendData = friendDoc.data();
            friendsData.push({
              id: friendId,
              username: friendData.username,
              firstName: friendData.firstName,
              lastName: friendData.lastName,
              profilePhoto: friendData.profilePhoto,
              hasAccess: authorizedUsers.includes(friendId)
            });
          }
        })
      );
      
      setFriends(friendsData);
    } catch (error) {
      console.error('Error al obtener amigos:', error);
      Alert.alert('Error', 'No se pudieron cargar tus amigos');
    } finally {
      setFriendsLoading(false);
    }
  };
  
  const saveRoutineSettings = async () => {
    try {
      setSavingSettings(true);
      
      const routineRef = doc(firestore, 'routines', routineId);
      
      // Generate a random code if sharing is enabled but no code exists
      let updatedCode = routineCode;
      if (publicSharing && !routineCode) {
        updatedCode = generateRoutineCode();
        setRoutineCode(updatedCode);
      }
      
      await updateDoc(routineRef, {
        shared: publicSharing,
        routineCode: publicSharing ? updatedCode : null
      });
      
      Alert.alert('Éxito', 'Configuración de compartir guardada correctamente');
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      Alert.alert('Error', 'No se pudo guardar la configuración');
    } finally {
      setSavingSettings(false);
    }
  };
  
  const generateRoutineCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };
  
  const toggleFriendAccess = async (friendId: string, currentAccess: boolean) => {
    try {
      const routineRef = doc(firestore, 'routines', routineId);
      const routineDoc = await getDoc(routineRef);
      
      if (!routineDoc.exists()) {
        Alert.alert('Error', 'No se encontró la rutina');
        return;
      }
      
      const routineData = routineDoc.data();
      let authorizedUsers = routineData.authorizedUsers || [];
      
      if (currentAccess) {
        // Remove access
        authorizedUsers = authorizedUsers.filter((id: string) => id !== friendId);
      } else {
        // Add access
        if (!authorizedUsers.includes(friendId)) {
          authorizedUsers.push(friendId);
          
          // Crear notificación solo cuando se concede acceso (no cuando se revoca)
          await createRoutineSharedNotification(friendId, routineId);
        }
      }
      
      await updateDoc(routineRef, { authorizedUsers });
      
      // Update local state
      setFriends(friends.map(friend => 
        friend.id === friendId 
          ? { ...friend, hasAccess: !currentAccess } 
          : friend
      ));
      
      Alert.alert('Éxito', currentAccess ? 'Acceso revocado' : 'Acceso concedido');
    } catch (error) {
      console.error('Error al actualizar acceso:', error);
      Alert.alert('Error', 'No se pudo actualizar el acceso');
    }
  };
  
  const shareRoutineCode = async () => {
    if (!routineCode) return;
    
    try {
      await Share.share({
        message: `¡Únete a mi rutina "${routine.name}" en GymBRO! Usa el código: ${routineCode}`,
      });
    } catch (error) {
      console.error('Error al compartir código:', error);
    }
  };
  
  const renderFriendItem = ({ item }: { item: FriendData }) => (
    <View style={styles.friendItem}>
      <View style={styles.friendInfo}>
        <View style={styles.avatarContainer}>
          {item.profilePhoto ? (
            <View style={styles.avatar} />
          ) : (
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>
                {item.firstName.charAt(0)}{item.lastName.charAt(0)}
              </Text>
            </View>
          )}
        </View>
        
        <View>
          <Text style={[styles.friendName, { color: theme.colors.text }]}>{item.firstName} {item.lastName}</Text>
          <Text style={[styles.friendUsername, { color: theme.colors.grey.dark }]}>@{item.username}</Text>
        </View>
      </View>
      
      <TouchableOpacity
        style={item.hasAccess ? [styles.accessButtonGranted, { backgroundColor: theme.colors.error }] : [styles.accessButtonRevoked, { backgroundColor: theme.colors.success }]}
        onPress={() => toggleFriendAccess(item.id, item.hasAccess)}
      >
        <Text style={[styles.accessTextGranted, { color: theme.colors.white }]}>
          {item.hasAccess ? 'Revocar' : 'Conceder'}
        </Text>
      </TouchableOpacity>
    </View>
  );
  
  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <Card style={styles.routineInfoCard}>
        <Text style={[styles.routineName, { color: theme.colors.text }]}>{routine.name}</Text>
        <Text style={[styles.routineDesc, { color: theme.colors.text }]}>{routine.description}</Text>
      </Card>
      
      <Card style={styles.sharingCard}>
        <View style={styles.sharingHeader}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Compartir Públicamente</Text>
          <Switch
            value={publicSharing}
            onValueChange={setPublicSharing}
            trackColor={{ false: theme.colors.grey.light, true: theme.colors.primary }}
            thumbColor={theme.colors.white}
          />
        </View>
        
        {publicSharing && (
          <View style={styles.codeContainer}>
            <Text style={[styles.codeLabel, { color: theme.colors.text }]}>Código de la Rutina:</Text>
            <View style={[styles.codeWrapper, { backgroundColor: theme.colors.grey.light }]}>
              <Text style={[styles.codeText, { color: theme.colors.primary }]}>{routineCode || 'Generando...'}</Text>
              <TouchableOpacity onPress={shareRoutineCode} disabled={!routineCode}>
                <Ionicons name="share-social-outline" size={20} color={theme.colors.primary} />
              </TouchableOpacity>
            </View>
            <Text style={[styles.codeHelp, { color: theme.colors.grey.dark }]}>
              Cualquier persona con este código podrá unirse a tu rutina
            </Text>
          </View>
        )}
        
        <Button
          title="Guardar Configuración"
          onPress={saveRoutineSettings}
          type="primary"
          loading={savingSettings}
          style={styles.saveButton}
        />
      </Card>
      
      <View style={styles.friendsSection}>
        <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Compartir con Amigos</Text>
        <Text style={[styles.sectionDescription, { color: theme.colors.grey.dark }]}>
          Concede o revoca acceso a tus amigos
        </Text>
        
        {friendsLoading ? (
          <ActivityIndicator size="small" color={theme.colors.primary} />
        ) : friends.length > 0 ? (
          <FlatList
            data={friends}
            renderItem={renderFriendItem}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.friendsList}
          />
        ) : (
          <View style={styles.emptyFriends}>
            <Text style={[styles.emptyText, { color: theme.colors.grey.dark }]}>No tienes amigos para compartir</Text>
            <Button
              title="Gestionar Amigos"
              onPress={() => navigation.navigate('FriendManagement')}
              type="outline"
              style={styles.manageFriendsButton}
            />
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: spacing.lg,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  routineInfoCard: {
    marginBottom: spacing.lg,
  },
  routineName: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginBottom: spacing.xs,
  },
  routineDesc: {
    fontSize: typography.fontSize.md,
  },
  sharingCard: {
    marginBottom: spacing.lg,
  },
  sharingHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  sectionDescription: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.md,
  },
  codeContainer: {
    marginBottom: spacing.md,
  },
  codeLabel: {
    fontSize: typography.fontSize.md,
    marginBottom: spacing.xs,
  },
  codeWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    marginBottom: spacing.xs,
  },
  codeText: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    letterSpacing: 2,
  },
  codeHelp: {
    fontSize: typography.fontSize.xs,
  },
  saveButton: {
    marginTop: spacing.sm,
  },
  friendsSection: {
    flex: 1,
  },
  friendsList: {
    marginTop: spacing.md,
  },
  friendItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey.light,
  },
  friendInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarContainer: {
    marginRight: spacing.md,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    color: colors.white,
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
  },
  friendName: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  friendUsername: {
    fontSize: typography.fontSize.sm,
  },
  accessButtonGranted: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.sm,
  },
  accessButtonRevoked: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.sm,
  },
  accessTextGranted: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
  },
  accessTextRevoked: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
  },
  emptyFriends: {
    alignItems: 'center',
    marginTop: spacing.xl,
  },
  emptyText: {
    fontSize: typography.fontSize.md,
    marginBottom: spacing.md,
  },
  manageFriendsButton: {
    width: '70%',
  },
});

export default RoutineSharingScreen; 